import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import { useDateFormatter } from './useDateFormatter';
import '../contracts/ContatosFidelizacao_SaveInput.ts';

import '../contracts/ContatosFidelizacao_BuscacontatosfidelizacaoporclienteidResponse';
import '../contracts/ContatosFidelizacao_CarregaContatoFidelizacaoResponse';
import '../contracts/ContatosFidelizacao_DeleteResponse';
import '../contracts/ContatosFidelizacao_SaveResponse';
import type { ContatosFidelizacao_SaveInput } from '../contracts/ContatosFidelizacao_SaveInput';
/**
 * Composable for Contato Fidelização (Customer Loyalty Contact) operations
 * Provides methods to interact with the Contatos Fidelização API
 */
export function useContatoFidelizacao() {
  const isLoading = ref(false);
  const error = ref(null);
  const { localDateTimeToApi } = useDateFormatter();

  /**
   * Fetch single contato fidelização by ID
   */
  const fetchContatoFidelizacao = async (contatoId: number) => {
    log('useContatoFidelizacao', 'fetchContatoFidelizacao', 'Fetching contato', contatoId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/contatos_fidelizacao/carrega_contato_fidelizacao/${contatoId}`, { responseContract: 'ContatosFidelizacao_CarregaContatoFidelizacaoResponse' });
      log('useContatoFidelizacao', 'fetchContatoFidelizacao', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao buscar contato');
      }
    } catch (err) {
      log('useContatoFidelizacao', 'fetchContatoFidelizacao', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Save contato fidelização (create or update)
   */
  const saveContatoFidelizacao = async (input: ContatosFidelizacao_SaveInput) => {
    log('useContatoFidelizacao', 'saveContatoFidelizacao', 'Saving contato', input);
    isLoading.value = true;
    error.value = null;

    try {
      // App → API boundary: browser-local datetime → ISO 8601 UTC
      const contatoData: ContatosFidelizacao_SaveInput = {
        ...input,
        ...(input.contatofidelizacao_data_cadastro
          ? { contatofidelizacao_data_cadastro: localDateTimeToApi(input.contatofidelizacao_data_cadastro) }
          : {}),
      };
      validate('ContatosFidelizacao_SaveInput', contatoData);
      const response = await httpService.post('/index.php/contatos_fidelizacao/save/', contatoData, { responseContract: 'ContatosFidelizacao_SaveResponse' });
      log('useContatoFidelizacao', 'saveContatoFidelizacao', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao salvar contato');
      }
    } catch (err) {
      log('useContatoFidelizacao', 'saveContatoFidelizacao', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch contatos fidelização by client ID
   */
  const fetchContatosByClienteId = async (clienteId: number) => {
    log('useContatoFidelizacao', 'fetchContatosByClienteId', 'Fetching contatos for client', clienteId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/contatos_fidelizacao/buscaContatosFidelizacaoPorClienteID/${clienteId}`, { responseContract: 'ContatosFidelizacao_BuscacontatosfidelizacaoporclienteidResponse' });
      log('useContatoFidelizacao', 'fetchContatosByClienteId', 'Response', response);

      if (response.success) {
        return response.dados || [];
      } else {
        throw new Error(response.error || 'Erro ao buscar contatos do cliente');
      }
    } catch (err) {
      log('useContatoFidelizacao', 'fetchContatosByClienteId', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Delete contato fidelização
   */
  const deleteContatoFidelizacao = async (contatoId: number) => {
    log('useContatoFidelizacao', 'deleteContatoFidelizacao', 'Deleting contato', contatoId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/contatos_fidelizacao/delete/${contatoId}`, { responseContract: 'ContatosFidelizacao_DeleteResponse' });
      log('useContatoFidelizacao', 'deleteContatoFidelizacao', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao deletar contato');
      }
    } catch (err) {
      log('useContatoFidelizacao', 'deleteContatoFidelizacao', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    // State
    isLoading,
    error,

    // Methods
    fetchContatoFidelizacao,
    fetchContatosByClienteId,
    saveContatoFidelizacao,
    deleteContatoFidelizacao
  };
}
